import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  Inbox, RefreshCw, ArrowLeft, Phone, Mail, MapPin, Calendar as CalIcon, Tag,
  Image as ImageIcon, X, Loader2, Flower2, Lock, LogOut, Check, Search,
  Download, List, CalendarDays, StickyNote, MessageSquareText, MailCheck,
  CalendarClock, Sun,
} from "lucide-react";
import { toast } from "sonner";
import { LotusMark } from "./LotusMark";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lotus_admin_token";
const STATUSES = ["New", "Contacted", "Booked"];
const STATUS_COLORS = { New: "#F56FB4", Contacted: "#f5c542", Booked: "#53C653" };

const toDate = (key) => new Date(`${key}T00:00:00`);
const prettyDate = (key) => { try { return format(toDate(key), "EEEE, MMMM d"); } catch { return key; } };

const apptMessage = (q) => {
  const first = (q.name || "there").split(" ")[0];
  const when = q.preferred_date ? format(toDate(q.preferred_date), "EEEE, MMMM d") : "your requested date";
  return `Hi ${first}, this is Lotus Property Services confirming your ${q.service || "cleanout"} appointment for ${when}. Reply with any questions — we look forward to helping your property bloom!`;
};

const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1174.7].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g);
      g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.12;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.14, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.start(t);
      o.stop(t + 0.32);
    });
  } catch (e) { /* audio unavailable */ }
};

/* ------------------------------ Login gate ------------------------------ */
const Login = ({ onAuthed }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onAuthed(res.data.token);
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Incorrect password" : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white noise-overlay flex items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit} data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-white/12 bg-[#161616]/80 backdrop-blur-xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <div className="flex flex-col items-center text-center">
          <LotusMark size={52} />
          <h1 className="font-heading font-extrabold text-2xl mt-4 uppercase tracking-tight">Lead Inbox</h1>
          <p className="text-white/50 text-sm mt-1">Enter the admin password to continue.</p>
        </div>
        <div className="relative mt-8">
          <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F56FB4]" />
          <input
            data-testid="admin-password-input"
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl bg-[#0d0d0d] border border-white/12 pl-11 pr-4 py-3.5 text-white placeholder-white/35 outline-none focus:border-[#F56FB4] transition-colors"
          />
        </div>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !pw}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F56FB4] px-6 py-3.5 font-semibold text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Enter Inbox"}
        </button>
        <Link to="/" className="mt-5 flex items-center justify-center gap-2 text-white/50 hover:text-white text-sm transition-colors">
          <ArrowLeft size={15} /> Back to site
        </Link>
      </motion.form>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-[#161616] px-6 py-5">
    <p className="font-heading font-extrabold text-4xl" style={{ color: accent }}>{value}</p>
    <p className="text-sm text-white/50 mt-1">{label}</p>
  </div>
);

const StatusPill = ({ status }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
    style={{ background: `${STATUS_COLORS[status]}1f`, color: STATUS_COLORS[status] }}
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLORS[status] }} />
    {status}
  </span>
);

const StatusButtons = ({ current, onChange, testPrefix }) => (
  <div className="flex gap-1.5">
    {STATUSES.map((s) => (
      <button
        key={s}
        data-testid={testPrefix ? `${testPrefix}-${s.toLowerCase()}` : undefined}
        onClick={() => onChange(s)}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors border ${
          current === s ? "border-transparent" : "border-white/12 text-white/50 hover:text-white hover:border-white/30"
        }`}
        style={current === s ? { background: `${STATUS_COLORS[s]}1f`, color: STATUS_COLORS[s] } : {}}
      >
        {current === s && <Check size={12} />} {s}
      </button>
    ))}
  </div>
);

const RescheduleButton = ({ q, onReschedule, label = "Reschedule", testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white/70 hover:text-white hover:border-[#53C653]/60 transition-colors">
        <CalendarClock size={13} /> {label}
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-auto p-0 bg-[#161616] border-white/10">
      <Calendar
        mode="single"
        selected={q.preferred_date ? toDate(q.preferred_date) : undefined}
        onSelect={(d) => d && onReschedule(q.id, format(d, "yyyy-MM-dd"))}
        className="text-white"
      />
      {q.preferred_date && (
        <button onClick={() => onReschedule(q.id, "")} className="w-full text-center text-xs text-white/50 hover:text-[#F56FB4] py-2 border-t border-white/10">
          Clear date
        </button>
      )}
    </PopoverContent>
  </Popover>
);

const QuickReply = ({ q }) => {
  const msg = apptMessage(q);
  const sms = `sms:${q.phone}?&body=${encodeURIComponent(msg)}`;
  const mail = `mailto:${q.email}?subject=${encodeURIComponent("Your Lotus Property Services appointment")}&body=${encodeURIComponent(msg)}`;
  return (
    <div className="flex gap-2">
      <a data-testid={`quickreply-text-${q.id}`} href={sms} className="inline-flex items-center gap-1.5 rounded-full bg-[#53C653]/15 text-[#53C653] px-3 py-1.5 text-xs font-semibold hover:bg-[#53C653]/25 transition-colors">
        <MessageSquareText size={13} /> Text
      </a>
      <a data-testid={`quickreply-email-${q.id}`} href={mail} className="inline-flex items-center gap-1.5 rounded-full bg-[#F56FB4]/15 text-[#F56FB4] px-3 py-1.5 text-xs font-semibold hover:bg-[#F56FB4]/25 transition-colors">
        <MailCheck size={13} /> Email
      </a>
    </div>
  );
};

/* ------------------------------ Dashboard ------------------------------ */
const Dashboard = ({ token, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list");
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [calMonth, setCalMonth] = useState(new Date());
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const knownIds = useRef(null);

  const authAxios = useCallback(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      toast.error("Session expired. Please log in again.");
      onLogout();
      return true;
    }
    return false;
  }, [onLogout]);

  const fetchQuotes = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await authAxios().get("/quotes");
      const data = res.data || [];
      if (knownIds.current === null) {
        knownIds.current = new Set(data.map((q) => q.id));
      } else {
        const fresh = data.filter((q) => !knownIds.current.has(q.id));
        if (fresh.length > 0) {
          playChime();
          toast.success(fresh.length === 1 ? `New lead from ${fresh[0].name}! 🌸` : `${fresh.length} new leads just arrived! 🌸`);
          document.title = `(${fresh.length}) Lotus Leads`;
        }
        knownIds.current = new Set(data.map((q) => q.id));
      }
      setQuotes(data);
    } catch (err) {
      handleAuthError(err);
      if (!silent && err.response?.status !== 401) toast.error("Could not load leads.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authAxios, handleAuthError]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);
  useEffect(() => {
    const id = setInterval(() => fetchQuotes(true), 25000);
    return () => clearInterval(id);
  }, [fetchQuotes]);

  const openDetail = async (q) => {
    setActive(q);
    setDetail(null);
    setNoteDraft(q.notes || "");
    setDetailLoading(true);
    try {
      const res = await authAxios().get(`/quotes/${q.id}`);
      setDetail(res.data);
      setNoteDraft(res.data.notes || "");
    } catch (err) {
      if (!handleAuthError(err)) setDetail({ ...q, photos: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  const patchLocal = (id, patch) => {
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
    setActive((a) => (a && a.id === id ? { ...a, ...patch } : a));
  };

  const changeStatus = async (id, status) => {
    try {
      await authAxios().patch(`/quotes/${id}/status`, { status });
      patchLocal(id, { status });
      toast.success(`Marked as ${status}`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not update status.");
    }
  };

  const rescheduleDate = async (id, dateStr) => {
    try {
      await authAxios().patch(`/quotes/${id}/date`, { preferred_date: dateStr });
      patchLocal(id, { preferred_date: dateStr });
      toast.success(dateStr ? `Scheduled for ${format(toDate(dateStr), "PP")}` : "Date cleared");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not update the date.");
    }
  };

  const saveNotes = async (id) => {
    setSavingNote(true);
    try {
      await authAxios().patch(`/quotes/${id}/notes`, { notes: noteDraft });
      patchLocal(id, { notes: noteDraft });
      toast.success("Notes saved");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not save notes.");
    } finally {
      setSavingNote(false);
    }
  };

  const sendSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await authAxios().post("/admin/daily-summary");
      toast.success(`Summary emailed · ${res.data.booked_today} booked today, ${res.data.new_leads} new`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not send the summary email.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const exportCsv = () => {
    const headers = ["Name", "Phone", "Email", "Address", "Service", "Preferred Date", "Status", "Notes", "Description", "Photos", "Submitted"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = quotes.map((q) => [q.name, q.phone, q.email, q.address, q.service, q.preferred_date, q.status, (q.notes || "").replace(/\s+/g, " "), (q.description || "").replace(/\s+/g, " "), q.photo_count, q.created_at]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lotus-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Leads exported to CSV");
  };

  const fmt = (iso) => { try { return format(new Date(iso), "PP · p"); } catch { return iso; } };

  const term = search.trim().toLowerCase();
  const filtered = quotes.filter((q) => {
    const byStatus = filter === "All" || q.status === filter;
    const bySearch = !term || [q.name, q.phone, q.email, q.address, q.service].some((v) => (v || "").toLowerCase().includes(term));
    return byStatus && bySearch;
  });
  const totalPhotos = quotes.reduce((a, q) => a + (q.photo_count || 0), 0);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: quotes.filter((q) => q.status === s).length }), {});

  const bookedDates = useMemo(
    () => quotes.filter((q) => q.status === "Booked" && q.preferred_date).map((q) => toDate(q.preferred_date)),
    [quotes]
  );
  const selectedKey = format(selectedDay, "yyyy-MM-dd");
  const dayJobs = quotes.filter((q) => q.status === "Booked" && q.preferred_date === selectedKey);
  const unscheduled = quotes.filter((q) => q.status === "Booked" && !q.preferred_date);

  return (
    <div className="min-h-screen bg-[#111111] text-white noise-overlay">
      <header className="border-b border-white/10 bg-[#0d0d0d] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LotusMark size={36} />
            <div className="leading-none">
              <span className="block font-heading font-extrabold tracking-tight text-lg">LOTUS · LEADS</span>
              <span className="block text-[9px] tracking-[0.3em] text-[#53C653] font-semibold">ESTIMATE INBOX</span>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button data-testid="admin-summary" onClick={sendSummary} disabled={summaryLoading} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-[#f5c542] transition-colors disabled:opacity-60">
              {summaryLoading ? <Loader2 size={16} className="animate-spin" /> : <Sun size={16} />} <span className="hidden md:inline">Email Summary</span>
            </button>
            <button data-testid="admin-export" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-[#53C653] transition-colors">
              <Download size={16} /> <span className="hidden md:inline">Export</span>
            </button>
            <button data-testid="admin-refresh" onClick={() => fetchQuotes()} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-[#53C653] transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> <span className="hidden md:inline">Refresh</span>
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-[#F56FB4] px-4 py-2 text-sm font-semibold">
              <LogOut size={16} /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10" onMouseDown={() => { document.title = "Lotus Property Services"; }}>
        <h1 className="font-heading font-extrabold uppercase text-3xl md:text-4xl tracking-tight">Estimate Requests</h1>
        <p className="text-white/50 mt-2">Every lead submitted through the website, newest first.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
          <Stat label="Total Leads" value={quotes.length} accent="#F56FB4" />
          <Stat label="Photos Attached" value={totalPhotos} accent="#53C653" />
          <Stat label="Booked" value={counts.Booked} accent="#53C653" />
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3 mt-8">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, email, address, or service..."
              className="w-full rounded-full bg-[#0d0d0d] border border-white/12 pl-11 pr-10 py-3 text-white placeholder-white/35 outline-none focus:border-[#F56FB4] transition-colors"
            />
            {search && (
              <button data-testid="admin-search-clear" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                <X size={16} />
              </button>
            )}
          </div>
          <div className="flex rounded-full border border-white/12 p-1 bg-[#0d0d0d] self-start">
            <button data-testid="admin-view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${view === "list" ? "bg-white text-[#111111]" : "text-white/60 hover:text-white"}`}>
              <List size={15} /> List
            </button>
            <button data-testid="admin-view-calendar" onClick={() => setView("calendar")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${view === "calendar" ? "bg-white text-[#111111]" : "text-white/60 hover:text-white"}`}>
              <CalendarDays size={15} /> Calendar
            </button>
          </div>
        </div>

        {view === "list" ? (
          <>
            <div className="flex flex-wrap gap-2 mt-6" data-testid="admin-filters">
              {["All", ...STATUSES].map((f) => (
                <button
                  key={f}
                  data-testid={`admin-filter-${f.toLowerCase()}`}
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
                    filter === f ? "bg-white text-[#111111] border-white" : "border-white/15 text-white/70 hover:border-white/40"
                  }`}
                >
                  {f} {f !== "All" && <span className="opacity-60">· {counts[f]}</span>}
                </button>
              ))}
            </div>

            <div className="mt-6">
              {loading ? (
                <div className="flex items-center gap-3 text-white/50 py-20 justify-center"><Loader2 className="animate-spin" /> Loading leads...</div>
              ) : filtered.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-white/15 py-20 flex flex-col items-center text-center">
                  <Inbox size={40} className="text-white/30" />
                  <p className="mt-4 text-white/60 font-heading font-semibold text-lg">{term ? "No matching leads" : "No leads yet"}</p>
                  <p className="text-white/40 text-sm mt-1">{term ? "Try a different search." : "New estimate requests will appear here."}</p>
                </div>
              ) : (
                <div className="grid gap-4" data-testid="admin-leads-list">
                  {filtered.map((q) => (
                    <div
                      key={q.id}
                      data-testid={`admin-lead-${q.id}`}
                      className="group rounded-2xl border border-white/10 bg-[#161616]/70 backdrop-blur-xl p-5 md:p-6 transition-colors hover:border-[#F56FB4]/50"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <button onClick={() => openDetail(q)} className="flex items-center gap-4 text-left">
                          <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#F56FB4] to-[#53C653] flex items-center justify-center font-heading font-extrabold">
                            {(q.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-heading font-bold text-lg flex items-center gap-2">
                              {q.name}
                              {q.notes && <StickyNote size={14} className="text-[#f5c542]" title="Has notes" />}
                            </p>
                            <p className="text-sm text-white/50">{fmt(q.created_at)}</p>
                          </div>
                        </button>
                        <div className="flex flex-wrap items-center gap-2">
                          {q.service && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 text-white/70 px-3 py-1 text-xs"><Tag size={12} />{q.service}</span>}
                          {q.photo_count > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 text-white/70 px-3 py-1 text-xs"><ImageIcon size={12} />{q.photo_count}</span>}
                          <StatusPill status={q.status} />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                        <div className="flex flex-wrap gap-4 text-sm text-white/70">
                          <a href={`tel:${q.phone}`} className="inline-flex items-center gap-2 hover:text-white"><Phone size={14} className="text-[#F56FB4]" />{q.phone}</a>
                          <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 hover:text-white truncate"><Mail size={14} className="text-[#53C653]" />{q.email}</a>
                        </div>
                        <StatusButtons current={q.status} onChange={(s) => changeStatus(q.id, s)} testPrefix={`admin-setstatus-${q.id}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="grid lg:grid-cols-[auto_1fr] gap-8 mt-6" data-testid="admin-calendar-view">
            <div className="rounded-3xl border border-white/10 bg-[#161616] p-4 self-start">
              <Calendar
                mode="single"
                selected={selectedDay}
                onSelect={(d) => d && setSelectedDay(d)}
                month={calMonth}
                onMonthChange={setCalMonth}
                modifiers={{ booked: bookedDates }}
                modifiersClassNames={{ booked: "cal-booked" }}
                className="text-white"
              />
              <div className="flex items-center gap-2 mt-3 px-2 text-xs text-white/50">
                <span className="h-2 w-2 rounded-full bg-[#53C653]" /> Days with booked jobs
              </div>
            </div>

            <div>
              <h3 className="font-heading font-bold text-xl">{prettyDate(selectedKey)}</h3>
              {dayJobs.length === 0 ? (
                <p className="text-white/50 mt-3">No booked jobs scheduled for this day.</p>
              ) : (
                <div className="grid gap-3 mt-4">
                  {dayJobs.map((q) => (
                    <div key={q.id} className="rounded-2xl border border-[#53C653]/30 bg-[#53C653]/5 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <button onClick={() => openDetail(q)} className="font-heading font-bold text-left hover:text-[#53C653] transition-colors">{q.name}</button>
                        <StatusPill status={q.status} />
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-white/60 mt-2">
                        <span className="inline-flex items-center gap-1.5"><Phone size={13} className="text-[#F56FB4]" />{q.phone}</span>
                        {q.service && <span className="inline-flex items-center gap-1.5"><Tag size={13} className="text-[#53C653]" />{q.service}</span>}
                        {q.address && <span className="inline-flex items-center gap-1.5 truncate"><MapPin size={13} className="text-[#F56FB4]" />{q.address}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <QuickReply q={q} />
                        <RescheduleButton q={q} onReschedule={rescheduleDate} label="Change date" testid={`cal-reschedule-${q.id}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {unscheduled.length > 0 && (
                <div className="mt-8">
                  <p className="text-sm uppercase tracking-wide text-white/40 mb-3">Booked · no date set ({unscheduled.length})</p>
                  <div className="grid gap-2" data-testid="admin-unscheduled">
                    {unscheduled.map((q) => (
                      <div key={q.id} className="flex items-center justify-between gap-3 rounded-2xl border border-white/12 px-4 py-3">
                        <button onClick={() => openDetail(q)} className="text-left text-white/80 hover:text-white transition-colors">
                          {q.name}{q.service ? ` · ${q.service}` : ""}
                        </button>
                        <button
                          data-testid={`schedule-here-${q.id}`}
                          onClick={() => rescheduleDate(q.id, selectedKey)}
                          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#53C653]/15 text-[#53C653] px-3 py-1.5 text-xs font-semibold hover:bg-[#53C653]/25 transition-colors"
                        >
                          <CalendarClock size={13} /> Schedule for {format(selectedDay, "MMM d")}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-detail-modal"
              className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl border border-white/12 bg-[#161616] p-6 md:p-8"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-extrabold text-2xl">{active.name}</p>
                  <p className="text-sm text-white/50">{fmt(active.created_at)}</p>
                </div>
                <button data-testid="admin-detail-close" onClick={() => setActive(null)} className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#F56FB4]">
                  <X size={18} />
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
                <StatusButtons current={active.status} onChange={(s) => changeStatus(active.id, s)} />
                <div className="flex flex-wrap items-center gap-2">
                  <QuickReply q={active} />
                  <RescheduleButton q={active} onReschedule={rescheduleDate} testid={`detail-reschedule-${active.id}`} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                {[
                  { Icon: Phone, label: "Phone", val: active.phone, c: "#F56FB4", href: `tel:${active.phone}` },
                  { Icon: Mail, label: "Email", val: active.email, c: "#53C653", href: `mailto:${active.email}` },
                  { Icon: MapPin, label: "Address", val: active.address || "—", c: "#F56FB4" },
                  { Icon: Tag, label: "Service", val: active.service || "—", c: "#53C653" },
                  { Icon: CalIcon, label: "Preferred Date", val: active.preferred_date ? prettyDate(active.preferred_date) : "—", c: "#F56FB4" },
                ].map(({ Icon, label, val, c, href }) => (
                  <div key={label} className="rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-white/40 flex items-center gap-1.5"><Icon size={13} style={{ color: c }} />{label}</p>
                    {href ? <a href={href} className="text-white mt-1 break-words block hover:text-[#F56FB4]">{val}</a> : <p className="text-white mt-1 break-words">{val}</p>}
                  </div>
                ))}
              </div>

              {active.description && (
                <div className="rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 mt-4">
                  <p className="text-xs uppercase tracking-wide text-white/40">Description</p>
                  <p className="text-white/85 mt-1 leading-relaxed">{active.description}</p>
                </div>
              )}

              {/* Private notes */}
              <div className="mt-4">
                <p className="font-heading font-bold text-sm uppercase tracking-wide text-white/60 mb-2 flex items-center gap-1.5"><StickyNote size={14} className="text-[#f5c542]" /> Private Notes</p>
                <textarea
                  data-testid="admin-notes-input"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Jot call outcomes, quoted price, follow-up reminders..."
                  className="w-full min-h-[90px] rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-white/90 placeholder-white/30 outline-none focus:border-[#f5c542] transition-colors resize-y"
                />
                <div className="flex justify-end mt-2">
                  <button
                    data-testid="admin-notes-save"
                    onClick={() => saveNotes(active.id)}
                    disabled={savingNote || noteDraft === (active.notes || "")}
                    className="inline-flex items-center gap-2 rounded-full bg-[#f5c542] px-5 py-2 text-sm font-semibold text-[#111111] disabled:opacity-50 transition-opacity"
                  >
                    {savingNote ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save notes
                  </button>
                </div>
              </div>

              <div className="mt-6">
                <p className="font-heading font-bold text-sm uppercase tracking-wide text-white/60 mb-3">Photos</p>
                {detailLoading ? (
                  <div className="flex items-center gap-2 text-white/50 py-6"><Loader2 size={16} className="animate-spin" /> Loading photos...</div>
                ) : detail && detail.photos && detail.photos.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="admin-detail-photos">
                    {detail.photos.map((src, i) => (
                      <a key={i} href={src} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-white/10 aspect-square">
                        <img src={src} alt={`attachment ${i + 1}`} className="h-full w-full object-cover" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm inline-flex items-center gap-2"><Flower2 size={15} /> No photos attached.</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ------------------------------ Entry ------------------------------ */
export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onAuthed={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
